import { useEffect, useState } from 'react';
import { Mail, MessageSquare, Phone, Globe, Loader2 } from 'lucide-react';
import { DynamicForm, schemaToFormConfig } from '@centraoneplus/ui';
import type { DynamicFormSectionConfig } from '@centraoneplus/ui';
import type { TicketType } from '@centraoneplus/shared-types';
import { ticketTypesService } from '@services/ticket-types.service';
import { teamMembersService, type TeamMember } from '@services/team-members.service';
import { pipelineService } from '@services/pipeline.service';
import { TicketTypeSelector } from './TicketTypeSelector';
import { TicketTagInput } from './TicketTagInput';
import { getFieldKind, validateFieldValue } from './fieldValidation';
import {
  COUNTRY_CODE_REQUIRED_MESSAGE,
  PHONE_INVALID_MESSAGE,
  isValidPhoneNumber,
  parsePhone,
  phoneToSave,
  sanitizePhoneInput,
} from '@/lib/phone';
import { CountryCodeSelect } from '@components/CountryCodeSelect';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { useAuthStore } from '@stores/authStore';
import { ASSIGNEE_LOCKED_HINT, canChangeAssignee } from '@/features/tickets/assignmentPermissions';

/**
 * Normalised values produced by the form. The parent maps these to the right
 * service DTO (CreateTicketDto on create, UpdateTicketDto on edit).
 */
export interface TicketFormValues {
  subject: string;
  description?: string;
  channel: string;
  priority: string;
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
  customerId?: string;
  tags: string[];
  ticketTypeId?: string;
  assigneeId?: string;
  // Edit-mode-only fields that mirror the ticket-detail sidebar. Undefined on
  // create (the backend assigns pipeline/stage/status from the ticket type).
  status?: string;
  pipelineId?: string;
  stageId?: string;
  customFields: Record<string, any>;
}

interface TicketFormProps {
  mode: 'create' | 'edit';
  initialValues?: Partial<TicketFormValues>;
  onSubmit: (values: TicketFormValues) => Promise<void> | void;
  onCancel: () => void;
  isLoading?: boolean;
  submitLabel?: string;
  /**
   * Fires whenever the user first edits any field (and stays true thereafter,
   * until the form remounts). Lets the parent gate its Save button so nothing
   * can be submitted until an actual change is made. Only user interaction
   * flips this — programmatic seeding (schema defaults) does not.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * 'modal' (default) — compact single-column layout for the popup.
   * 'page'  — two-column desktop layout (main content + properties panel).
   */
  variant?: 'modal' | 'page';
  /**
   * Replaces the ticket-type grid. The create page picks the type on its own
   * screen first and passes a compact summary here, so the details form isn't
   * pushed below a full grid of type cards.
   */
  ticketTypeSlot?: React.ReactNode;
}

const CHANNELS = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'phone', label: 'Phone', icon: Phone },
  { id: 'web', label: 'Web Form', icon: Globe },
];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200' },
  { id: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { id: 'high', label: 'High', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  { id: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
];

// Consolidated ticket statuses (mirrors the sidebar + backend TICKET_STATUSES).
const STATUSES = [
  { id: 'new', label: 'New' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'pending', label: 'Pending' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'closed', label: 'Closed' },
];

// Single source of truth for the contact-email check so the live (onChange/
// onBlur) feedback and the submit-time validate() never disagree.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Shared create/edit ticket form. Channel and customer contact fields are only
 * shown on create — the update API (UpdateTicketDto) doesn't accept them, so
 * they're omitted in edit mode.
 */
export function TicketForm({
  mode,
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel,
  variant = 'modal',
  onDirtyChange,
  ticketTypeSlot,
}: TicketFormProps) {
  const isEdit = mode === 'edit';
  const isPage = variant === 'page';

  // Flipped to true on the first user edit; reported up so the parent can keep
  // Save disabled until something actually changes. One-way (never resets to
  // false) — a field edited back to its original value still counts as touched.
  const [dirty, setDirty] = useState(false);
  const markDirty = () => setDirty(true);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const [subject, setSubject] = useState(initialValues?.subject ?? '');
  const [description, setDescription] = useState(initialValues?.description ?? '');
  // Default channel for a manually-created ticket is Web Form (agents opening a
  // ticket from the UI). Edit mode always seeds this from the loaded ticket, so
  // the fallback only applies on create.
  const [channel, setChannel] = useState(initialValues?.channel ?? 'web');
  const [priority, setPriority] = useState(initialValues?.priority ?? 'medium');
  const [contactEmail, setContactEmail] = useState(initialValues?.contactEmail ?? '');
  const [contactName, setContactName] = useState(initialValues?.contactName ?? '');
  const initialPhone = parsePhone(initialValues?.contactPhone);
  const [phoneCode, setPhoneCode] = useState(initialPhone.countryCode);
  const [phoneNumber, setPhoneNumber] = useState(initialPhone.national);
  const [tags, setTags] = useState<string[]>(initialValues?.tags ?? []);
  const [ticketTypeId, setTicketTypeId] = useState<string | undefined>(initialValues?.ticketTypeId);
  const [assigneeId, setAssigneeId] = useState<string | undefined>(initialValues?.assigneeId);
  // Editing a ticket that already has an assignee: only admins and supervisors
  // may change or clear it. Create is never locked.
  const currentUser = useAuthStore((s) => s.user);
  const assigneeLocked = isEdit && !canChangeAssignee(currentUser, initialValues?.assigneeId);
  // Status / pipeline / stage — edit mode only, mirroring the ticket-detail
  // sidebar. Seeded from the loaded ticket; all three route through the single
  // update PATCH so a save touching several of them collapses into one grouped
  // history entry server-side.
  const [status, setStatus] = useState<string | undefined>(initialValues?.status);
  const [pipelineId, setPipelineId] = useState<string | undefined>(initialValues?.pipelineId);
  const [stageId, setStageId] = useState<string | undefined>(initialValues?.stageId);
  const [pipelines, setPipelines] = useState<Array<{ id: string; name: string }>>([]);
  const [stages, setStages] = useState<Array<{ id: string; name: string; sortOrder?: number }>>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>(
    initialValues?.customFields ?? {},
  );

  // Load the pipeline list once (edit mode only) for the Pipeline selector.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    pipelineService
      .getPipelines()
      .then((res) => {
        if (!cancelled) setPipelines((res ?? []).map((p) => ({ id: p.id, name: p.name })));
      })
      .catch(() => {
        // Non-critical — the Pipeline selector just stays hidden if this fails.
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  // Load the stages for the currently-selected pipeline (edit mode only) so the
  // Stage selector always reflects the chosen pipeline's lanes.
  useEffect(() => {
    if (!isEdit || !pipelineId) {
      setStages([]);
      return;
    }
    let cancelled = false;
    pipelineService
      .getStages(pipelineId)
      .then((res) => {
        if (cancelled) return;
        setStages(
          (res ?? [])
            .map((s: any) => ({ id: s.id, name: s.name, sortOrder: s.sortOrder }))
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
        );
      })
      .catch(() => {
        if (!cancelled) setStages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, pipelineId]);

  // Active team members for the Assignee dropdown (shown on create AND edit).
  const [agents, setAgents] = useState<TeamMember[]>([]);
  useEffect(() => {
    let cancelled = false;
    teamMembersService
      .getTeamMembers({ status: 'active', limit: 100 })
      .then((res) => {
        if (!cancelled) setAgents(res.data ?? []);
      })
      .catch(() => {
        // Non-critical — the dropdown just shows "Unassigned" if this fails.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const memberLabel = (m: TeamMember) =>
    m.displayName || [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || m.email;

  const [formSections, setFormSections] = useState<DynamicFormSectionConfig[]>([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load the dynamic custom-field schema whenever the selected ticket type
  // changes (and on first mount in edit mode for the existing type).
  useEffect(() => {

    let cancelled = false;
    // Custom fields are shown/edited on CREATE only. In edit mode we neither
    // load the schema nor render the fields; the ticket's existing customFields
    // pass through untouched on save. (Custom-field edits happen elsewhere —
    // the Line Items tab on the ticket detail page.)
    if (isEdit || !ticketTypeId) {
      setFormSections([]);
      return;
    }
    (async () => {
      try {
        setIsLoadingSchema(true);
        const schema = await ticketTypesService.getFormSchema(ticketTypeId);
        if (cancelled) return;
        setFormSections(schemaToFormConfig(schema));
        // Seed each schema field's value. Existing values (from the ticket or
        // its line items) win; we never clobber them. Line items often use a
        // different key casing than the custom field (e.g. `brokerEmail` vs
        // `broker_email`), so match on a normalized key before falling back to
        // the field's configured default.
        setCustomFieldValues((prev) => {
          const next = { ...prev };
          const normalize = (k: string) => k.toLowerCase().replace(/[^a-z0-9]/g, '');
          const byNormalizedKey: Record<string, any> = {};
          for (const [k, v] of Object.entries(prev)) {
            if (byNormalizedKey[normalize(k)] === undefined) byNormalizedKey[normalize(k)] = v;
          }
          const isEmpty = (v: any) => v === undefined || v === null || v === '';
          schema.fieldGroups.forEach((group) => {
            group.fields.forEach((field) => {
              if (!isEmpty(next[field.key])) return; // already has a value
              const matched = byNormalizedKey[normalize(field.key)];
              if (!isEmpty(matched)) {
                next[field.key] = matched;
              } else if (field.defaultValue != null) {
                next[field.key] = field.defaultValue;
              }
            });
          });
          return next;
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load form schema:', err);
          setFormSections([]);
        }
      } finally {
        if (!cancelled) setIsLoadingSchema(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketTypeId, isEdit]);
  
  const handleTicketTypeChange = (ticketType: TicketType | null) => {
    setTicketTypeId(ticketType?.id);
    if (!ticketType) setCustomFieldValues({});
    setErrors({});
    markDirty();
  };

  // Clear a single field's error (e.g. once the user starts fixing it), so the
  // red "… is required" message disappears as the requirement is fulfilled.
  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const handleCustomFieldChange = (key: string, value: any) => {
    // Phone fields: drop letters as they're typed, so text can't even be entered.
    const field = formSections.flatMap((sec) => sec.fields).find((f) => f.key === key);
    if (field && typeof value === 'string' && getFieldKind(key, field.fieldType) === 'phone') {
      value = sanitizePhoneInput(value);
    }
    setCustomFieldValues((prev) => ({ ...prev, [key]: value }));
    clearError(key);
    markDirty();
  };

  const validate = async (): Promise<boolean> => {
    const next: Record<string, string> = {};

    if (!subject.trim()) next.subject = 'Subject is required';

    // Ticket type is mandatory on both create and edit.
    if (!ticketTypeId) {
      next.ticketType = 'Please select a ticket type';
    }

    // Contact email is only collected/required on create.
    if (!isEdit) {
      if (!contactEmail.trim()) {
        next.contactEmail = 'Customer email is required';
      } else if (!EMAIL_RE.test(contactEmail.trim())) {
        next.contactEmail = 'Invalid email format';
      }
    }

    // Optional, and checked only if it changed: an old number with no code can
    // stay as it is while the rest of the ticket is edited.
    if (phoneNumber && (phoneNumber !== initialPhone.national || phoneCode !== initialPhone.countryCode)) {
      if (!phoneCode) next.contactPhone = COUNTRY_CODE_REQUIRED_MESSAGE;
      else if (!isValidPhoneNumber(phoneNumber)) next.contactPhone = PHONE_INVALID_MESSAGE;
    }

    // Custom fields (create only — edit doesn't render them). Validate required
    // fields, then type-aware format (date / URL / email / number / 13-digit
    // MPAN) using the same shared rules as the line-item editor, so a bad value
    // can't be saved at creation.
    for (const section of formSections) {
      for (const field of section.fields) {
        if (field.isVisible === false) continue;
        const raw = customFieldValues[field.key];
        const val = raw == null ? '' : String(raw).trim();
        if (field.isRequired && val === '') {
          next[field.key] = `${field.name} is required`;
          continue;
        }
        const msg = validateFieldValue(getFieldKind(field.key, field.fieldType), val);
        if (msg) next[field.key] = msg;
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(await validate())) return;

    // On create, include EVERY defined custom field — even unfilled ones (as
    // empty) — so the new ticket's line item lists the full field set and the
    // user can complete or leave them later (the Line Items tab is never empty
    // for a typed ticket).
    let customFields = customFieldValues;
    if (!isEdit && formSections.length > 0) {
      const full: Record<string, any> = { ...customFieldValues };
      formSections.forEach((section) =>
        section.fields.forEach((field) => {
          if (full[field.key] === undefined || full[field.key] === null) {
            full[field.key] = '';
          }
        }),
      );
      customFields = full;
    }

    await onSubmit({
      subject: subject.trim(),
      description: description.trim() || undefined,
      channel,
      priority,
      contactEmail: contactEmail.trim() || undefined,
      contactName: contactName.trim() || undefined,
      contactPhone: phoneToSave(initialValues?.contactPhone, phoneCode, phoneNumber) || undefined,
      customerId: initialValues?.customerId,
      tags,
      ticketTypeId,
      // Chosen assignee (create and edit). Empty selection → undefined; the
      // parent decides how to apply it (create: omit → auto-assign; edit: only
      // when changed, via the dedicated assign/unassign endpoint).
      assigneeId,
      // Edit-only sidebar-parity fields. Undefined on create.
      status,
      pipelineId,
      stageId,
      customFields,
    });
  };

  const sectionLabel = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

  // ── Field blocks (defined once, arranged differently per variant) ──────────

  const ticketTypeBlock = ticketTypeSlot ?? (
    <TicketTypeSelector
      value={ticketTypeId}
      onChange={handleTicketTypeChange}
      disabled={isLoading}
      required
      error={errors.ticketType}
    />
  );

  // Channel — selectable on create, read-only when editing.
  const channelBlock = (
    <div>
      <label className={`${sectionLabel} mb-2`}>
        Channel
        {isEdit && (
          <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">(read-only)</span>
        )}
      </label>
      {/* An even grid: four buttons in a wrapping row broke 3 + 1. */}
      <div className="grid grid-cols-2 gap-2">
        {CHANNELS.filter((c) => !isEdit || c.id === channel).map((c) => {
          const Icon = c.icon;
          const active = channel === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                if (!isEdit) setChannel(c.id);
              }}
              disabled={isLoading || isEdit}
              className={`flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg border transition-colors ${
                active
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300'
              } ${isEdit ? 'cursor-default' : ''}`}
            >
              <Icon className="h-4 w-4" />
              <span className="text-sm">{c.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  // A pasted "+91 98765 43210" fills in both the code and the number.
  const handlePhoneChange = (value: string) => {
    const pasted = value.trim().startsWith('+') ? parsePhone(value) : null;
    if (pasted?.countryCode) {
      setPhoneCode(pasted.countryCode);
      setPhoneNumber(pasted.national);
    } else {
      setPhoneNumber(value.replace(/\D/g, '').slice(0, 15));
    }
    clearError('contactPhone');
  };

  // Customer info — email and name are editable on create, read-only when
  // editing. The phone stays editable, so a number can be added to a ticket later.
  // Stacks in the page's narrow side panel; two columns in the modal.
  const customerBlock = (
    <div className={isPage ? 'space-y-4' : 'grid grid-cols-2 gap-4'}>
      <div>
        <label className={`${sectionLabel} mb-1`}>Customer Email{!isEdit && ' *'}</label>
        <input
          type="email"
          value={contactEmail}
          onChange={(e) => {
            setContactEmail(e.target.value);
            // Clear the error once a valid-looking email is entered.
            if (EMAIL_RE.test(e.target.value.trim())) {
              clearError('contactEmail');
            }
          }}
          // Surface the format error as soon as the user leaves the field, so a
          // bad email is flagged without waiting for a submit attempt.
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && !EMAIL_RE.test(v)) {
              setErrors((prev) => ({ ...prev, contactEmail: 'Invalid email format' }));
            }
          }}
          disabled={isLoading || isEdit}
          readOnly={isEdit}
          className={`input-field ${errors.contactEmail ? 'border-red-500' : ''} ${
            isEdit ? 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 cursor-not-allowed' : ''
          }`}
          placeholder="customer@example.com"
        />
        {errors.contactEmail && <p className="mt-1 text-sm text-red-500">{errors.contactEmail}</p>}
      </div>
      <div>
        <label className={`${sectionLabel} mb-1`}>Customer Name</label>
        <input
          type="text"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          disabled={isLoading || isEdit}
          readOnly={isEdit}
          className={`input-field ${
            isEdit ? 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 cursor-not-allowed' : ''
          }`}
          placeholder="John Doe"
        />
      </div>
      <div>
        <label htmlFor="ticket-contact-phone" className={`${sectionLabel} mb-1`}>Customer Phone</label>
        <div className="flex gap-2">
          <CountryCodeSelect
            value={phoneCode}
            onChange={(code) => {
              setPhoneCode(code);
              clearError('contactPhone');
            }}
          />
          <input
            id="ticket-contact-phone"
            type="tel"
            inputMode="tel"
            value={phoneNumber}
            onChange={(e) => handlePhoneChange(e.target.value)}
            disabled={isLoading}
            className={`input-field min-w-0 ${errors.contactPhone ? 'border-red-500' : ''}`}
            placeholder="9876543210"
          />
        </div>
        {errors.contactPhone && <p className="mt-1 text-sm text-red-500">{errors.contactPhone}</p>}
      </div>
    </div>
  );

  const subjectBlock = (
    <div>
      <label className={`${sectionLabel} mb-1`}>Subject *</label>
      <input
        type="text"
        value={subject}
        onChange={(e) => {
          setSubject(e.target.value);
          markDirty();
          if (e.target.value.trim()) clearError('subject');
        }}
        // The API rejects subjects over 500 characters.
        maxLength={500}
        className={`input-field ${errors.subject ? 'border-red-500' : ''}`}
        placeholder="Brief description of the issue"
      />
      {errors.subject && <p className="mt-1 text-sm text-red-500">{errors.subject}</p>}
    </div>
  );

  const descriptionBlock = (
    <div>
      <label className={`${sectionLabel} mb-1`}>Description</label>
      <textarea
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
          markDirty();
        }}
        className={`input-field resize-y ${isPage ? 'min-h-[200px]' : 'min-h-[120px]'}`}
        placeholder="Detailed description of the issue..."
        rows={isPage ? 8 : 4}
      />
    </div>
  );

  const priorityBlock = (
    <div>
      <label className={`${sectionLabel} mb-2`}>Priority</label>
      <div className="flex gap-2 flex-wrap">
        {PRIORITIES.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => {
              setPriority(p.id);
              markDirty();
            }}
            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
              priority === p.id
                ? p.color + ' ring-2 ring-offset-1 ring-current'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );

  const customFieldsBlock = isLoadingSchema ? (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading form fields...</span>
    </div>
  ) : formSections.length > 0 ? (
    <DynamicForm
      // DynamicForm prints `section.icon` as text, and the schema stores an icon
      // NAME ("clipboard-list"), so the header would read the raw string.
      sections={formSections.map((s) => ({ ...s, icon: undefined }))}
      values={customFieldValues}
      errors={errors}
      onChange={handleCustomFieldChange}
      disabled={isLoading}
      columns={2}
    />
  ) : null;

  // Assignee — shown on both create and edit.
  const assigneeBlock = (
    <div>
      <label className={`${sectionLabel} mb-1`}>Assignee</label>
      <ThemedSelect
        value={assigneeId ?? ''}
        onChange={(e) => {
          setAssigneeId(e.target.value || undefined);
          markDirty();
        }}
        disabled={isLoading || assigneeLocked}
        title={assigneeLocked ? ASSIGNEE_LOCKED_HINT : undefined}
        className="input-field w-full"
      >
        <option value="">{isEdit ? 'Unassigned' : 'Unassigned (auto-assign)'}</option>
        {agents.map((m) => (
          <option key={m.id} value={m.id}>
            {memberLabel(m)}
          </option>
        ))}
      </ThemedSelect>
      {assigneeLocked && (
        <p className="mt-1 text-xs text-[var(--co-ink-4)]" data-testid="assignee-locked-hint">
          {ASSIGNEE_LOCKED_HINT}. You can add secondary agents from the ticket.
        </p>
      )}
    </div>
  );

  // Status — edit mode only (create derives status from the ticket type/pipeline).
  const statusBlock = (
    <div>
      <label className={`${sectionLabel} mb-1`}>Status</label>
      <ThemedSelect
        value={status ?? ''}
        onChange={(e) => {
          setStatus(e.target.value || undefined);
          markDirty();
        }}
        disabled={isLoading}
        className="input-field w-full"
      >
        {STATUSES.map((s) => (
          <option key={s.id} value={s.id}>
            {s.label}
          </option>
        ))}
      </ThemedSelect>
    </div>
  );

  // Pipeline — edit mode only. Switching pipeline resets the stage selection:
  // stages are pipeline-scoped, so the old stage can't carry over. The backend
  // relocates the ticket to the new pipeline's first stage when no stage is sent.
  const pipelineBlock = pipelines.length > 0 && (
    <div>
      <label className={`${sectionLabel} mb-1`}>Pipeline</label>
      <ThemedSelect
        value={pipelineId ?? ''}
        onChange={(e) => {
          const next = e.target.value || undefined;
          setPipelineId(next);
          setStageId(undefined); // stage belongs to the previous pipeline
          markDirty();
        }}
        disabled={isLoading}
        className="input-field w-full"
      >
        {pipelines.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </ThemedSelect>
    </div>
  );

  // Stage — edit mode only, scoped to the selected pipeline.
  const stageBlock = pipelineId && stages.length > 0 && (
    <div>
      <label className={`${sectionLabel} mb-1`}>Stage</label>
      <ThemedSelect
        value={stageId ?? ''}
        onChange={(e) => {
          setStageId(e.target.value || undefined);
          markDirty();
        }}
        disabled={isLoading}
        className="input-field w-full"
      >
        <option value="">Unset</option>
        {stages.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </ThemedSelect>
    </div>
  );

  const tagsBlock = (
    <div>
      <label className={`${sectionLabel} mb-1`}>Tags</label>
      <TicketTagInput
        value={tags}
        onChange={(next) => {
          setTags(next);
          markDirty();
        }}
        disabled={isLoading}
        placeholder="Pick a tag or type a new one..."
      />
    </div>
  );

  const actions = (
    <div className="flex justify-end gap-3">
      <button type="button" onClick={onCancel} className="btn-secondary" disabled={isLoading}>
        Cancel
      </button>
      <button type="submit" className="btn-primary" disabled={isLoading || isLoadingSchema}>
        {isLoading
          ? isEdit
            ? 'Saving...'
            : 'Creating...'
          : submitLabel || (isEdit ? 'Save Changes' : 'Create Ticket')}
      </button>
    </div>
  );

  // Page variant: floating cards, matching the ticket detail page.
  const pageCard =
    'rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] shadow-[var(--co-shadow-card)]';

  // ── Page layout: main content + a "Properties" side panel ──────────────────
  // The submit button lives in the page header (outside this component) and is
  // wired to this form via the shared `ticket-form` id.
  if (isPage) {
    return (
      <form id="ticket-form" onSubmit={handleSubmit} className="co-ticket-form space-y-[14px]">
        <div className={`${pageCard} p-5 sm:p-6`}>{ticketTypeBlock}</div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-[14px] items-start">
          {/* Main content */}
          <div className={`${pageCard} p-5 sm:p-6 space-y-6 lg:col-span-2`}>
            {subjectBlock}
            {descriptionBlock}
            {customFieldsBlock}
          </div>

          {/* Properties panel */}
          <div className={`${pageCard} p-5 sm:p-6 space-y-5 lg:col-span-1 lg:sticky lg:top-[92px]`}>
            <h3 className="text-[15px] font-semibold text-[var(--co-ink)]">Properties</h3>
            {isEdit && statusBlock}
            {isEdit && pipelineBlock}
            {isEdit && stageBlock}
            {priorityBlock}
            {assigneeBlock}
            {channelBlock}
            {customerBlock}
            {tagsBlock}
          </div>
        </div>
      </form>
    );
  }

  // ── Modal layout: compact single column ────────────────────────────────────
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {ticketTypeBlock}
      {channelBlock}
      {customerBlock}
      {subjectBlock}
      {descriptionBlock}
      {isEdit && statusBlock}
      {isEdit && pipelineBlock}
      {isEdit && stageBlock}
      {priorityBlock}
      {assigneeBlock}
      {customFieldsBlock}
      {tagsBlock}
      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">{actions}</div>
    </form>
  );
}
